import { forwardRef, Fragment, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { DisclosureNextBreadcrumb } from '@sas/react-svg';
import cx from 'classnames';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { useRefArray } from '../../hooks/useRefArray';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { useIsRTL } from '../../locale';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderDesignTokens, useDesignTokens } from '../../theme';
import { postfixTestId } from '../../util/testId';
import { Icon } from '../Icon';
import { Scrollable } from '../Scrollable';
import { GlobalSettingsKeys } from '../Settings';
import { useGetGlobalSetting } from '../Settings/GlobalSettingsProvider';
import { MultiBreadcrumbDesign, SingleBreadcrumbDesign } from './BreadcrumbItem';
import bundle from './NovaComponents-HorizontalBreadcrumb-gui-icu.properties';
import cn from './HorizontalBreadcrumb.css.json';
import './HorizontalBreadcrumb.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
const useAriaTextId = createUseHtmlId('AriaText');
export const HorizontalBreadcrumb = /*#__PURE__*/forwardRef((props, ref) => {
  const localize = useTextLocalizer();
  const listAriaTextId = useAriaTextId();
  const lastValueIndex = props.values ? props.values.length - 1 : -1;
  const [lastClickedIndex, setLastClickedIndex] = useState(undefined);
  const refIsMultiItemOpen = useRef(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(lastValueIndex);
  const [horizontalScrollIndex, setHorizontalScrollIndex] = useState(lastValueIndex);
  const [listMaxWidth, setListMaxWidth] = useState(400);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [setScrollableContentRef, getScrollableContent] = useResizeObserverRef(() => {
    const scrollableContent = getScrollableContent();
    if (!scrollableContent) return;
    setListMaxWidth(scrollableContent.clientWidth);
  });
  useEffect(() => {
    // If number of values changes, scroll to the last item in the list
    setHorizontalScrollIndex(lastValueIndex);
    setFocusedIndex(lastValueIndex);
  }, [lastValueIndex]);
  const dt = useDesignTokens();
  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useImperativeHandle(ref, () => ({
    focus: () => {
      breadcrumbRefs[focusedIndex]?.focus();
    }
  }));
  const tokens = {
    ...getHorizontalBreadcrumbDesignTokens(dt, {
      tokens: props.tokens,
      lastValueIndex,
      listMaxWidth,
      variant: props.variant
    }),
    ...props.theme?.(dt)
  };
  const handlePress = index => {
    setIsAnimating(true);
    setFocusedIndex(index);
    setTimeout(() => {
      props.onPress?.(index);
      setLastClickedIndex(undefined); // Store the last clicked index
    }, 300);
    setLastClickedIndex(index);
  };
  useEffect(() => {
    if (isAnimating) {
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isAnimating]);
  const isRTL = useIsRTL();
  const onKeyDown = event => {
    if (refIsMultiItemOpen.current) {
      return;
    }
    switch (event.key) {
      case isRTL ? 'ArrowRight' : 'ArrowLeft':
        {
          const nextFocusedIndex = Math.max(focusedIndex - 1, 0);
          setFocusedIndex(nextFocusedIndex);
          break;
        }
      case isRTL ? 'ArrowLeft' : 'ArrowRight':
        {
          const nextFocusedIndex = Math.min(focusedIndex + 1, lastValueIndex);
          setFocusedIndex(nextFocusedIndex);
          break;
        }
      case 'PageUp':
        {
          const nextFocusedIndex = Math.max(getNextPageFocusedItemIndex(refContent.current, breadcrumbRefs, 'back') ?? focusedIndex, 0);
          setFocusedIndex(nextFocusedIndex);
          break;
        }
      case 'PageDown':
        {
          const nextFocusedIndex = Math.min(getNextPageFocusedItemIndex(refContent.current, breadcrumbRefs, 'next') ?? focusedIndex, lastValueIndex);
          setFocusedIndex(nextFocusedIndex);
          break;
        }
      case 'Home':
        setFocusedIndex(0);
        break;
      case 'End':
        setFocusedIndex(lastValueIndex);
        break;
      default:
        return;
    }
    event.preventDefault();
  };
  const onFocus = () => event => {
    if (event.currentTarget.contains(event.target) && event.relatedTarget !== null &&
    // the relatedTarget is null when the element's tabindex is undefined or -1 in Safari browser
    !event.currentTarget.contains(event.relatedTarget)) {
      setHorizontalScrollIndex(focusedIndex);
    }
  };
  const refContainer = useRef(null);
  const refContent = useRef(null);
  const setContainerRef = el => {
    refContainer.current = el;
    setScrollableContentRef(el);
  };
  const {
    refs: breadcrumbRefs,
    createSetRef: createBreadcrumbRef
  } = useRefArray();
  const hideAnimation = props.hideAnimation ?? false;
  const shouldPlayAnimation = useGetGlobalSetting(GlobalSettingsKeys.PLAY_VISUAL_EFFECTS) === 'true' && !hideAnimation && !isFirstRender;
  useEffect(() => {
    if (refContainer.current?.contains(document.activeElement)) {
      breadcrumbRefs[focusedIndex]?.focus();
    }
  }, [breadcrumbRefs, focusedIndex]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(Scrollable, {
      ref: setContainerRef,
      contentRef: refContent,
      currentIndex: horizontalScrollIndex,
      setCurrentIndex: setHorizontalScrollIndex,
      separator: /*#__PURE__*/_jsx(Icon, {
        className: cx(cn.itemIcon, cn.opening),
        theme: () => {
          return {
            '--icon-default-override-fg': 'var(--breadcrumb-separator-icon-fg)'
          };
        },
        icon: DisclosureNextBreadcrumb
      }),
      contentClassName: cn.list,
      itemClassName: index => cx(cn.listItem, {
        [cn.singleCrumb]: !!props.values?.[index].isSingle
      }),
      contentProps: {
        onKeyDown,
        onFocus,
        'aria-label': props.ariaLabel ?? localize('list.aria.label')
      },
      style: {
        ...tokens,
        ...props.style
      },
      selectedIndex: props.onPress ? horizontalScrollIndex : undefined,
      variant: props.variant === 'primary-light' ? 'primary-light' : 'standard',
      contentAriaDescribedBy: listAriaTextId,
      allowOrderedList: true,
      testId: postfixTestId(props.testId, 'scrollable'),
      suppressScrollOnBlur: true,
      children: props.values?.map((value, i) => {
        const focused = i === focusedIndex;
        const breadcrumbRef = createBreadcrumbRef(i);
        const isLastItem = i === (props.values?.length ?? 0) - 1;
        let isAnimated = false;
        if (lastClickedIndex !== undefined && i > lastClickedIndex) {
          isAnimated = true;
        }
        return value.isSingle ? /*#__PURE__*/_jsx(SingleBreadcrumbDesign, {
          index: i,
          ref: breadcrumbRef,
          isLastItem: isLastItem,
          animated: isAnimated,
          focused: i === focusedIndex,
          isCurrent: value.isCurrent ?? i === lastValueIndex,
          hideAnimation: shouldPlayAnimation,
          value: value.values[value.selectedIndex],
          onPress: handlePress,
          variant: props.variant,
          testId: postfixTestId(props.testId, `breadcrumbItem-${i}`),
          onFocus: () => {
            setHorizontalScrollIndex(i);
          },
          tokens: props.tokens,
          icon: value.icon,
          ariaCurrent: props.ariaCurrent
        }, i) : /*#__PURE__*/_jsx(MultiBreadcrumbDesign, {
          ref: breadcrumbRef,
          index: i,
          isLastItem: isLastItem,
          animated: isAnimated,
          isCurrent: value.isCurrent ?? i === lastValueIndex,
          focused: focused,
          hideAnimation: shouldPlayAnimation,
          values: value.values,
          onOpen: index => {
            refIsMultiItemOpen.current = true;
            setFocusedIndex(index);
            props.onOpen?.(index);
          },
          onClose: index => {
            refIsMultiItemOpen.current = false;
            setFocusedIndex(index);
          },
          onPress: handlePress,
          onPressItem: props.onPressItem,
          selectedIndex: value.selectedIndex,
          onFocus: () => {
            setHorizontalScrollIndex(i);
          },
          variant: props.variant,
          testId: postfixTestId(props.testId, `breadcrumbItem-${i}`),
          theme: props.theme,
          tokens: props.tokens,
          ariaCurrent: props.ariaCurrent
        }, i);
      })
    }), /*#__PURE__*/_jsx("span", {
      id: listAriaTextId,
      hidden: true,
      children: localize('list.aria')
    })]
  });
});

// First, create a map in which full visible items are marked as true, otherwise as false.
// From this map can get the first/last item (base on the operation is next or back) in next page easily.
// Then, get the last/first item in next page by comparing the possible items' total wdith to container's width
function getNextPageFocusedItemIndex(container, items, direction) {
  if (!container) return null;
  const containerRect = container.getBoundingClientRect();
  const itemVisibilityMap = items.map(item => {
    if (!item) return false;
    const itemRect = item.getBoundingClientRect();
    return itemRect.left >= containerRect.left && itemRect.left + itemRect.width <= containerRect.left + containerRect.width;
  });
  const startIndex = direction === 'back' ? itemVisibilityMap.indexOf(true) - 1 : itemVisibilityMap.lastIndexOf(true) + 1;
  const endIndex = direction === 'back' ? 0 : items.length - 1;
  const step = direction === 'back' ? -1 : 1;
  let itemsWidth = 0;
  let focusIndex = endIndex;
  for (let i = startIndex; i !== endIndex; i += step) {
    itemsWidth += items[i]?.getBoundingClientRect().width ?? 0;
    if (itemsWidth > containerRect.width) {
      focusIndex = i - step;
      break;
    }
    itemsWidth += 14; // plus separator width which is a fixed value
  }

  return focusIndex;
}
export const getHorizontalBreadcrumbDesignTokens = (dt, opts) => {
  const itemIconFG = opts?.tokens?.().fg ?? (dt.themeVariant === 'light' && opts.variant === 'primary-light' ? dt.tokens['color-text-primary-inverse'] : dt.tokens['color-action-text-control-normal']);
  return {
    ...renderDesignTokens({
      'separator-icon-fg': itemIconFG,
      'squish-xs': `${dt.tokens['space-base-xxs']} ${dt.tokens['space-base-xs']}`,
      'squish-s': `${dt.tokens['space-base-xs']} ${dt.tokens['space-base-s']}`,
      'anim-dur-enter': dt.tokens['duration-250'],
      'anim-tf-enter': dt.tokens['ease-in'],
      'mg-xs': dt.tokens['space-base-xs']
    }, 'breadcrumb'),
    ...(!dt.enableNovaTokens && getHorizontalBreadcrumbLegacyDesignTokens(dt, opts))
  };
};
export const getHorizontalBreadcrumbLegacyDesignTokens = (dt, opts) => {
  const itemIconFG = opts?.tokens?.().fg ?? (dt.themeVariant === 'light' && opts.variant === 'primary-light' ? dt.c.white : dt.reportTokens.stateStandard.normal.fg);
  return renderDesignTokens({
    'separator-icon-fg': itemIconFG
  }, 'breadcrumb');
};
//# sourceMappingURL=HorizontalBreadcrumb.js.map